import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box, Typography, Skeleton, TableContainer,
    Table, TableHead, TableRow, TableCell, TableBody, Alert, Paper,
    Tooltip, IconButton, FormControlLabel, Switch, Slider, Collapse, LinearProgress, Button
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import { LineChart, type LineSeries } from '@mui/x-charts/LineChart';
import { useXScale, useYScale } from '@mui/x-charts/hooks';
import { logEvent } from '../analytics';
import { getBackendUrl } from '../config';
import { authenticatedFetch } from '../utils/apiClient';
import { ScoredGame } from '../types';

const formatRank = (rank: number): string => {
    if (rank % 100 >= 11 && rank % 100 <= 13) {
        return `${rank}th`;
    }
    switch (rank % 10) {
        case 1: return `${rank}st`;
        case 2: return `${rank}nd`;
        case 3: return `${rank}rd`;
        default: return `${rank}th`;
    }
};

const formatWeekToDate = (weekStart: Date, week: number): string => {
    const weekEndDate = new Date(weekStart.getTime());
    weekEndDate.setUTCDate(weekEndDate.getUTCDate() + (week * 7) - 1);
    return " " + weekEndDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    }) + " ";
};

const generateYAxisTicks = (maxRank: number): number[] => {
    const ticks = new Set<number>();
    ticks.add(1);
    ticks.add(4);
    if (maxRank >= 8) {
        ticks.add(8);
    }
    if (maxRank >= 12 && (maxRank - 12) > 2) {
        ticks.add(12);
    }
    ticks.add(maxRank);
    
    return Array.from(ticks).sort((a, b) => a - b);
};

interface RankErrorBarsProps {
    data: {
        week: number;
        rank_p10?: number | null;
        rank_p90?: number | null;
    }[];
}

const RankErrorBars: React.FC<RankErrorBarsProps> = ({ data }) => {
    const xScale = useXScale();
    const yScale = useYScale();
    if (!xScale || !yScale) return null;

    const capWidth = 4; // 8px total horizontal cap width

    return (
        <g className="rank-error-bars" style={{ pointerEvents: 'none' }}>
            {data.map((point) => {
                if (point.rank_p10 == null || point.rank_p90 == null) return null;
                const x = xScale(point.week);
                const yTop = yScale(point.rank_p10);
                const yBottom = yScale(point.rank_p90);

                if (
                    typeof x !== 'number' || isNaN(x) ||
                    typeof yTop !== 'number' || isNaN(yTop) ||
                    typeof yBottom !== 'number' || isNaN(yBottom)
                ) {
                    return null;
                }

                return (
                    <g key={point.week} stroke="#8884d8" strokeWidth={1.5} opacity={0.75}>
                        {/* Vertical whisker line connecting p10 (top) to p90 (bottom) */}
                        <line x1={x} y1={yTop} x2={x} y2={yBottom} />
                        {/* Top cap (best case / p10 rank) */}
                        <line x1={x - capWidth} y1={yTop} x2={x + capWidth} y2={yTop} strokeWidth={2} />
                        {/* Bottom cap (worst case / p90 rank) */}
                        <line x1={x - capWidth} y1={yBottom} x2={x + capWidth} y2={yBottom} strokeWidth={2} />
                    </g>
                );
            })}
        </g>
    );
};

const formatTeamNameForDisplay = (name: string) => {
    const trimmedName = name.trim();
    const CHAR_LIMIT = 12;
    if (trimmedName.length <= CHAR_LIMIT) return trimmedName;

    const middle = Math.floor(trimmedName.length / 2);
    let bestSplitIndex = -1;
    let minDistance = trimmedName.length;

    // Find the space closest to the middle
    for (let i = 0; i < trimmedName.length; i++) {
        if (trimmedName[i] === ' ') {
            const distance = Math.abs(i - middle);
            if (distance < minDistance) {
                minDistance = distance;
                bestSplitIndex = i;
            }
        }
    }

    if (bestSplitIndex !== -1) {
        return (
            <>
                {trimmedName.substring(0, bestSplitIndex)}
                <br />
                {trimmedName.substring(bestSplitIndex + 1)}
            </>
        );
    }
    return trimmedName;
};

export interface KrachRanking {
    rank: number;
    krach: number;
    team: string;
    gp: number;
    w: number;
    l: number;
    t: number;
    otw: number;
    otl: number;
    sos: number;
    expWins: number;
    winDiff: number;
}

export interface PredictedStanding {
    rank: number;
    team: string;
    wins: number;
    losses: number;
    ties: number;
    points: number;
    rank_p10?: number;
    rank_p90?: number;
    rank_median?: number;
    points_p10?: number;
    points_p90?: number;
}

export interface KrachData {
    last_updated: string;
    rankings: KrachRanking[];
    predictions: Record<string, Record<string, string | null>>;
}

interface KrachRankingsProps {
    seasonConfig: {
        seasonId: string;
        divisionId: string;
        key: string;
        label: string;
        startSchedule: string;
        endSchedule: string;
        teamName?: string;
    };
    numTeams: number;
    isPreseason?: boolean;
    previousSeasonConfig?: {
        seasonId: string;
        divisionId: string;
        key: string;
        label: string;
        startSchedule: string;
        endSchedule: string;
        teamName?: string;
    } | null;
    parentLoading?: boolean;
    allGames: ScoredGame[];
    krachData: KrachData | null;
    krachLoading: boolean;
    krachError: string | null;
    isWeighted: boolean;
    onWeightedChange: (weighted: boolean) => void;
    dateRange: [Date, Date] | null;
    onDateRangeChange: (range: [Date, Date] | null) => void;
}

const KrachRankings: React.FC<KrachRankingsProps> = ({ seasonConfig, numTeams, isPreseason, previousSeasonConfig, allGames, parentLoading = false, krachData, krachLoading, krachError, isWeighted, onWeightedChange, dateRange, onDateRangeChange }) => {
    const [hoveredCell, setHoveredCell] = useState<{ row: number; col: number } | null>(null);
    const [clickedCell, setClickedCell] = useState<{ row: number; col: number } | null>(null);
    const [predictedStandings, setPredictedStandings] = useState<PredictedStanding[]>([]);
    const [predictedLoading, setPredictedLoading] = useState(true);
    const [jetsPredictionTrend, setJetsPredictionTrend] = useState<{
        week: number;
        rank: number;
        rank_p10?: number;
        rank_p90?: number;
        points: number;
    }[]>([]);
    const [trendLoading, setTrendLoading] = useState(true);
    const [trendError, setTrendError] = useState<string | null>(null);
    const [trendProgress, setTrendProgress] = useState(0);
    const [showDateFilter, setShowDateFilter] = useState(false);
    const trendChartRef = useRef<HTMLDivElement | null>(null);
    const [isTrendVisible, setIsTrendVisible] = useState(false);

    const teamName = seasonConfig.teamName || 'Jets';

    const maxKrach = useMemo(() => 
        krachData ? Math.max(...krachData.rankings.map(r => r.krach)) : 0, 
    [krachData]);

    const totalSeasonWeeks = useMemo(() => {
        if (parentLoading || isPreseason || !allGames || allGames.length === 0) {
            return 0;
        }
        const gameDates = allGames.map(g => new Date(g.game.date)).filter(d => !isNaN(d.getTime()));
        if (gameDates.length === 0) {
            return 0;
        }
        const minTimestamp = Math.min(...gameDates.map(d => d.getTime()));
        const seasonEndDate = new Date(seasonConfig.endSchedule);
        if (isNaN(seasonEndDate.getTime())) {
            return 0;
        }
        const diffTime = Math.abs(seasonEndDate.getTime() - minTimestamp);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
    }, [allGames, parentLoading, isPreseason, seasonConfig.endSchedule]);

    const { minDate, maxDate, totalWeeks } = useMemo(() => {
        if (parentLoading || isPreseason || !allGames || allGames.length === 0) {
            return { minDate: null, maxDate: null, totalWeeks: 0 };
        }
        const gameDates = allGames
            .filter(g => g.game && g.game.date)
            .map(g => new Date(g.game.date))
            .filter(d => !isNaN(d.getTime()));

        if (gameDates.length < 2) {
            return { minDate: null, maxDate: null, totalWeeks: 0 };
        }

        const minTimestamp = Math.min(...gameDates.map(d => d.getTime()));
        const maxTimestamp = Math.max(...gameDates.map(d => d.getTime()));

        const min = new Date(minTimestamp);
        // Get day of week, where Sunday is 0, Monday is 1, etc.
        const dayOfWeek = min.getUTCDay(); 
        // Adjust to get the Monday of the week of the first game.
        // If the first game is on Sunday (day 0), we go back 6 days to the previous Monday.
        // Otherwise, we go back (dayOfWeek - 1) days.
        const offset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        min.setUTCDate(min.getUTCDate() - offset);
        min.setUTCHours(0, 0, 0, 0);

        const max = new Date(maxTimestamp);
        max.setUTCHours(23, 59, 59, 999); // Ensure we are at the end of the day

        const endDayOfWeek = max.getUTCDay(); // 0=Sun, 6=Sat
        const daysToAdd = endDayOfWeek === 0 ? 0 : 7 - endDayOfWeek; // Find the following Sunday
        max.setUTCDate(max.getUTCDate() + daysToAdd);

        const weeks = Math.ceil((max.getTime() - min.getTime()) / (1000 * 60 * 60 * 24 * 7));

        return { minDate: min, maxDate: max, totalWeeks: weeks };
    }, [allGames, isPreseason, parentLoading]);

    const [sliderValue, setSliderValue] = useState<[number, number]>([0, 0]);

    useEffect(() => {
        setSliderValue([0, totalWeeks]);
    }, [totalWeeks]);

    const handleSliderChange = (event: Event, newValue: number | number[]) => {
        setSliderValue(newValue as [number, number]);
    };

    const formatDate = (date: Date) => date.toISOString().split('T')[0];

    const handleSliderChangeCommitted = (event: React.SyntheticEvent | Event, newValue: number | number[]) => {
        if (minDate && maxDate && Array.isArray(newValue)) {
            const [startWeek, endWeek] = newValue as [number, number];

            if (startWeek === 0 && endWeek === totalWeeks) {
                if (dateRange !== null) {
                    onDateRangeChange(null);
                    logEvent('reset_krach_date_filter', { season: seasonConfig.label });
                }
                return;
            }

            const start = new Date(minDate.getTime());
            start.setUTCDate(start.getUTCDate() + startWeek * 7);

            const end = endWeek === totalWeeks ? new Date(maxDate.getTime()) : new Date(minDate.getTime());
            if (endWeek !== totalWeeks) {
                // TODO: I don't understand the how the range of the
                // slider relates to the min and max dates
                end.setUTCDate(end.getUTCDate() + (endWeek * 7) + 1);
                end.setUTCMilliseconds(end.getUTCMilliseconds() - 1);
            }

            onDateRangeChange([start, end]);
            logEvent('filter_krach_by_date', {
                start_date: formatDate(start),
                end_date: formatDate(end),
                season: seasonConfig.label
            });
        }
    };

    useEffect(() => {
        let isMounted = true;
        const fetchAllData = async () => {
            if (isPreseason) {
                if (isMounted) setPredictedLoading(false);
                return;
            }
            setPredictedLoading(true);
            try {
                const predictUrl = new URL(`${getBackendUrl()}/api/predict_standings`);
                predictUrl.searchParams.append('krachSeasonId', seasonConfig.seasonId);
                predictUrl.searchParams.append('krachDivisionId', seasonConfig.divisionId);
                predictUrl.searchParams.append('scheduleSeasonId', seasonConfig.seasonId);
                predictUrl.searchParams.append('scheduleDivisionId', seasonConfig.divisionId);
                predictUrl.searchParams.append('weighted', isWeighted.toString());
                if (dateRange) {
                    predictUrl.searchParams.append('startDate', formatDate(dateRange[0]));
                    predictUrl.searchParams.append('endDate', formatDate(dateRange[1]));
                }

                const predictResponse = await authenticatedFetch(predictUrl.toString());

                if (!isMounted) return;

                if (!predictResponse.ok) {
                    const errorData = await predictResponse.json();
                    throw new Error(`Prediction Error: ${errorData.error || predictResponse.status}`);
                }
                const predictData = await predictResponse.json();

                if (isMounted) {
                    setPredictedStandings(predictData);
                }
            } catch (e: any) {
                if (isMounted) {
                    console.error("Failed to fetch predicted standings:", e);
                }
            } finally {
                if (isMounted) {
                    setPredictedLoading(false);
                }
            }
        };

        fetchAllData();

        return () => {
            isMounted = false;
        };
    }, [seasonConfig, isPreseason, isWeighted, dateRange]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsTrendVisible(true);
                    if (trendChartRef.current) {
                        observer.unobserve(trendChartRef.current);
                    }
                }
            },
            {
                rootMargin: '0px',
                threshold: 0.1, // Start loading when 10% of the component is visible
            }
        );

        if (trendChartRef.current) {
            observer.observe(trendChartRef.current);
        }

        return () => {
            if (trendChartRef.current) {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                observer.unobserve(trendChartRef.current);
            }
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        if (!isTrendVisible) {
            return; // Don't fetch until visible
        }

        if (isPreseason || !minDate || totalWeeks === 0 || parentLoading) {
            if (!parentLoading && isMounted) setTrendLoading(false);
            return;
        }

        const fetchTrendData = async () => {
            if (!isMounted) return;
            setTrendLoading(true);
            setTrendError(null);
            setJetsPredictionTrend([]);
            setTrendProgress(0);

            const krachSeason = isPreseason && previousSeasonConfig ? previousSeasonConfig : seasonConfig;

            let completed = 0;
            const handleProgress = (data: PredictedStanding[]): PredictedStanding[] => {
                if (isMounted) {
                    completed++;
                    setTrendProgress((completed / totalWeeks) * 100);
                }
                return data;
            };

            const promises: Promise<PredictedStanding[]>[] = [];
            for (let i = 1; i <= totalWeeks; i++) {
                const weekEndDate = new Date(minDate.getTime());
                // A "week" is from Monday to Sunday. The end of week `i` is `i` weeks after minDate.
                // We set it to the end of Sunday.
                weekEndDate.setUTCDate(weekEndDate.getUTCDate() + i * 7 - 1);
                weekEndDate.setUTCHours(23, 59, 59, 999);

                const predictUrl = new URL(`${getBackendUrl()}/api/predict_standings`);
                predictUrl.searchParams.append('krachSeasonId', krachSeason.seasonId);
                predictUrl.searchParams.append('krachDivisionId', krachSeason.divisionId);
                predictUrl.searchParams.append('scheduleSeasonId', seasonConfig.seasonId);
                predictUrl.searchParams.append('scheduleDivisionId', seasonConfig.divisionId);
                predictUrl.searchParams.append('weighted', isWeighted.toString());
                predictUrl.searchParams.append('startDate', formatDate(minDate));
                predictUrl.searchParams.append('endDate', formatDate(weekEndDate));

                const promise = authenticatedFetch(predictUrl.toString())
                    .then(res => {
                        if (!res.ok) {
                            return res.json().then(err => Promise.reject(err));
                        }
                        return res.json();
                    })
                    .then(handleProgress);
                promises.push(promise);
            }

            try {
                const weeklyPredictions: PredictedStanding[][] = await Promise.all(promises);
                if (isMounted) {
                    const trendData = weeklyPredictions.map((standings, index) => {
                        const week = index + 1;
                        const jetsStanding = standings.find((t: PredictedStanding) => t.team.includes(teamName));
                        return {
                            week,
                            rank: jetsStanding?.rank ?? null,
                            rank_p10: jetsStanding?.rank_p10,
                            rank_p90: jetsStanding?.rank_p90,
                            points: jetsStanding?.points ?? null,
                        };
                    }).filter(d => d.rank !== null);
                    
                    setJetsPredictionTrend(trendData as {
                        week: number;
                        rank: number;
                        rank_p10?: number;
                        rank_p90?: number;
                        points: number;
                    }[]);
                }
            } catch (e: any) {
                console.error("Failed to fetch prediction trend data", e);
                if (isMounted) setTrendError(e.message || 'Failed to load prediction trend.');
            } finally {
                if (isMounted) setTrendLoading(false);
            }
        };

        fetchTrendData();
        return () => { isMounted = false; };
    }, [isTrendVisible, minDate, totalWeeks, isPreseason, seasonConfig, previousSeasonConfig, isWeighted, parentLoading, teamName]);

     const predictionTeams = useMemo(() => krachData?.rankings.map(r => r.team) ?? [], [krachData]);

     const commonPrefix = useMemo(() => {
        if (predictionTeams.length < 2) return '';
        const sortedTeams = [...predictionTeams].sort();
        const first = sortedTeams[0];
        const last = sortedTeams[sortedTeams.length - 1];
        let i = 0;
        while (i < first.length && first.charAt(i) === last.charAt(i)) {
            i++;
        }
        return first.substring(0, i);
    }, [predictionTeams]);

     const marks = useMemo(() => {
        if (!minDate || totalWeeks <= 0) return [];
        const m = [];
        for (let i = 1; i <= totalWeeks; i++) {
            const weekEndDate = new Date(minDate.getTime() + (i * 7 - 1) * 24 * 60 * 60 * 1000);
            m.push({ value: i, label: `${weekEndDate.getUTCMonth() + 1}/${weekEndDate.getUTCDate()}` });
        }
        return m;
    }, [minDate, totalWeeks]);

    const sliderValueText = (value: number) => {
        if (!minDate) return '';
        const d = new Date(minDate.getTime());
        d.setUTCDate(d.getUTCDate() + value * 7);
        return `${d.toLocaleString('default', { month: 'short', timeZone: 'UTC' })} ${d.getUTCDate()}`;
    };

    const hasTrendData = jetsPredictionTrend && jetsPredictionTrend.length > 0;

    // 1. Conditionally create the dataset
    const chartDataset = useMemo(() => {
        const fullSeasonWeeks = totalSeasonWeeks > 0 ? totalSeasonWeeks : 1;
        // Create a base dataset for the full season
        const baseData = Array.from({ length: fullSeasonWeeks }, (_, i) => ({
            week: i + 1,
            playoffCutoff: 8 as number | null,
            rank: null as number | null, // Default rank to null
            rank_p10: null as number | null,
            rank_p90: null as number | null,
        }));

        if (hasTrendData) {
            // Merge the actual trend data into the base dataset
            jetsPredictionTrend.forEach(point => {
                if (point.week - 1 < baseData.length) {
                    baseData[point.week - 1].rank = point.rank;
                    baseData[point.week - 1].rank_p10 = point.rank_p10 ?? null;
                    baseData[point.week - 1].rank_p90 = point.rank_p90 ?? null;
                }
            });
        }
        return baseData;
    }, [totalSeasonWeeks, jetsPredictionTrend, hasTrendData]);
    
    // 2. Define the series array, only including the rank series if data exists
    const seriesConfig: LineSeries[] = [
      // This series is always present
      {
        id: 'playoffCutoffSeries',
        dataKey: 'playoffCutoff',
        color: 'red',
        showMark: false,
        disableHighlight: true,
      },
    ];
    
    if (hasTrendData) {
      seriesConfig.unshift({ // Add the main data line to the beginning
        dataKey: 'rank',
        label: `${teamName} Predicted Rank`,
        color: '#8884d8',
        curve: 'monotoneX',
        connectNulls: true,
        showMark: true,
        valueFormatter: (value: number | null, context: { dataIndex: number }) => {
            if (value === null) return '';
            const row = chartDataset[context.dataIndex];
            if (row && row.rank_p10 != null && row.rank_p90 != null) {
                const p10Str = formatRank(Math.round(row.rank_p10));
                const p90Str = formatRank(Math.round(row.rank_p90));
                return `${formatRank(value)} (80% CI: ${p10Str} – ${p90Str})`;
            }
            return formatRank(value);
        },
      });
    }    

    const yAxisMax = numTeams > 1 ? numTeams : 10;
    const yAxisTicks = generateYAxisTicks(yAxisMax);

    if (isPreseason) {
        return (
            <Paper variant="outlined" elevation={0} sx={{ p: { xs: 3, sm: 4 }, mt: 4, borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="h5" component="h2" gutterBottom fontWeight="bold">
                    Season Has Not Started
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    KRACH rankings, matchup probabilities, and end-of-season predictions will be available once games are played for this season.
                </Typography>
                {previousSeasonConfig && (
                    <Button
                        variant="outlined"
                        component={RouterLink}
                        to={`/standings/${previousSeasonConfig.key}`}
                        startIcon={<LeaderboardIcon />}
                        size="large"
                    >
                        View {previousSeasonConfig.label} Standings
                    </Button>
                )}
            </Paper>
        );
    }

    return (
        <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title="Filter by date range">
                        <IconButton
                            onClick={() => {
                                const newShowState = !showDateFilter;
                                setShowDateFilter(newShowState);
                                if (newShowState) {
                                    logEvent('show_krach_date_filter', { season: seasonConfig.label });
                                }
                            }}
                            aria-label="filter by date"
                            size="small"
                            sx={{ mr: 1 }}
                        >
                            <FilterListIcon />
                        </IconButton>
                    </Tooltip>
                    <Typography variant="h5" component="h2">
                        KRACH Rankings
                    </Typography>
                    <Tooltip title="The KRACH rating system is an attempt to combine the performance of each team with the strength of the opposition against which that performance was achieved, and to summarize the result as one number, a “rating”, for each team. The higher the rating, the better the team.">
                        <IconButton
                            component="a"
                            href="https://www.tier1hockeyfederation.com/krach-explained/"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => logEvent('click_krach_help')}
                            size="small"
                            aria-label="Learn more about KRACH rankings"
                            sx={{ ml: 0.5 }}
                        >
                            <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                        control={<Switch checked={isWeighted} onChange={(e) => {
                            onWeightedChange(e.target.checked);
                            logEvent('toggle_weighted_krach', { weighted: e.target.checked });
                        }} />}
                        label="Weighted"
                    />
                    <Tooltip
                        title={
                            <React.Fragment>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Ranking Method</Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Unweighted:</strong> Uses the original KRACH algorithm. Ranks teams based purely on wins and losses. A 2-1 win is treated the same as a 7-0 win.
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Weighted:</strong> Uses a modified version of the KRACH algorithm. Ranks teams based on the margin of victory. A dominant 7-0 win will boost a team's ranking more than a close 2-1 win.
                                </Typography>
                            </React.Fragment>
                        }
                    >
                        <IconButton size="small" aria-label="Learn more about weighted KRACH" sx={{ ml: -0.5 }}>
                            <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            <Collapse in={showDateFilter}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 4, // 16px for a more modern, MD3-style look
                        backgroundColor: 'action.hover',
                    }}
                >
                    <Typography gutterBottom id="date-range-slider-label">
                        Date Range Filter
                    </Typography>
                    {parentLoading ? (
                        <Skeleton variant="rectangular" height={24} sx={{ mt: 2, mb: 1 }}/>
                    ) : totalWeeks > 0 && minDate ? (
                        <Slider
                            aria-labelledby="date-range-slider-label"
                            value={sliderValue}
                            onChange={handleSliderChange}
                            onChangeCommitted={handleSliderChangeCommitted}
                            valueLabelFormat={sliderValueText}
                            getAriaValueText={sliderValueText}
                            valueLabelDisplay="auto"
                            min={0}
                            max={totalWeeks}
                            marks={marks.filter((_, i) => i % 4 === 0 || i === totalWeeks)}
                            step={1}
                            disabled={krachLoading || isPreseason}
                            sx={{
                                // M3 specifies a 4px track height.
                                height: 4,
                                // The color of the track, thumb, and state layers.
                                color: 'primary.main',
                                // Remove the default padding.
                                padding: '18px 0',
                                '& .MuiSlider-rail': {
                                height: 4,
                                borderRadius: 2,
                                // The inactive track color should be Surface Container Highest.
                                // Using a simple grey for broad theme compatibility.
                                backgroundColor: (theme) => theme.palette.mode === 'light' ? '#E7E0EC' : '#49454F',
                                opacity: 1,
                                },
                                '& .MuiSlider-track': {
                                height: 4,
                                borderRadius: 2,
                                // The border is not needed in M3.
                                border: 'none',
                                },
                                '& .MuiSlider-thumb': {
                                // M3 specifies a 20px handle.
                                width: 20,
                                height: 20,
                                backgroundColor: 'primary.main',
                                // This removes the M2 box-shadow and applies a subtle M3-style one.
                                boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.3), 0px 1px 3px 1px rgba(0, 0, 0, 0.15)',
                                '&:before': {
                                    // Hide the default ripple effect.
                                    display: 'none',
                                },
                                // This pseudo-element creates the state layer halo.
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    // Center the halo on the thumb.
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    backgroundColor: 'currentColor',
                                    opacity: 0,
                                    // Add a transition for a smooth effect.
                                    transition: 'opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                                },
                                '&:hover, &.Mui-focusVisible': {
                                    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.3), 0px 1px 3px 1px rgba(0, 0, 0, 0.15)',
                                    // Show the state layer on hover and focus.
                                    '&::after': {
                                        opacity: 0.08, // M3 state layer opacity for hover is 8%
                                    }
                                },
                                // Styles for the active (dragged) state.
                                '&.Mui-active': {
                                    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.3), 0px 1px 3px 1px rgba(0, 0, 0, 0.15)',
                                    '&::after': {
                                        opacity: 0.12, // M3 state layer opacity for pressed is 12%
                                    }
                                },
                                },
                                '& .MuiSlider-valueLabel': {
                                // M3 value label is a squircle shape.
                                borderRadius: '8px 8px 8px 0px',
                                // Use a color from the M3 spec, like Inverse Surface.
                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#E6E0E9' : '#312F33',
                                color: (theme) => theme.palette.mode === 'dark' ? '#312F33' : '#F5EFF7',
                                // Use M3 typography styles.
                                typography: 'body2',
                                fontWeight: 500,
                                lineHeight: '1.2',
                                padding: '6px 8px',
                                // This is a trick to create the "tail" of the value label.
                                '&:before': {
                                    display: 'none'
                                },
                                // You might need to adjust positioning slightly based on your layout.
                                top: -38,
                                },
                            }}
                        />
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            {isPreseason ? "Date filter available after season starts." : `Not enough game data to create date range. ${totalWeeks} / ${minDate}`}
                        </Typography>
                    )}
                </Paper>
            </Collapse>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
                {krachError && (
                    <Alert severity="error" sx={{ m: 1 }}>
                        Could not load KRACH data: {krachError}
                    </Alert>
                )}
                {!krachLoading && !krachData && !krachError && (
                     <Alert severity="info" sx={{ m: 1 }}>No KRACH data available.</Alert>
                )}
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Rank</TableCell>
                            <TableCell sx={{ pr: 0 }}>KRACH</TableCell>
                            <TableCell sx={{ width: '30%', pl: 2 }}>Rating</TableCell>
                            <TableCell>Team</TableCell>
                            <TableCell>GP</TableCell>
                            <TableCell>W</TableCell>
                            <TableCell>L</TableCell>
                            <TableCell>T</TableCell>
                            <TableCell>OTL</TableCell> 
                        </TableRow>
                    </TableHead>
                    <TableBody>
                    {krachLoading ? (
                            Array.from(new Array(numTeams)).map((_, index) => (
                                <TableRow key={`krach-skeleton-${index}`}>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell sx={{ pr: 0 }}><Skeleton /></TableCell>
                                    <TableCell sx={{ pl: 2 }}><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell> 
                                </TableRow>
                            ))
                        ) : (
                            krachData?.rankings.map((team) => {
                                const barWidth = maxKrach > 0 ? (team.krach / maxKrach) * 100 : 0;
                                return (
                                    <TableRow key={team.rank}>
                                        <TableCell>{team.rank}</TableCell>
                                        <TableCell sx={{ pr: 0 }}>{team.krach}</TableCell>
                                        <TableCell sx={{ pl: 2 }}>
                                            <Box sx={{ width: '100%', backgroundColor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
                                                <Box
                                                    sx={{
                                                        width: `${barWidth}%`,
                                                        height: '18px',
                                                        backgroundColor: '#8884d8',
                                                        borderRadius: 1,
                                                        transition: 'width 0.5s ease-in-out',
                                                    }}
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell>{team.team}</TableCell>
                                        <TableCell>{team.gp}</TableCell>
                                        <TableCell>{team.w + team.otw}</TableCell>
                                        <TableCell>{team.l}</TableCell>
                                        <TableCell>{team.t}</TableCell>
                                        <TableCell>{team.otl}</TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="h5" component="h2" gutterBottom>Match Up Probability</Typography>
             <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
                <Table
                    size="small"
                    sx={{ tableLayout: 'fixed', minWidth: 850 }}
                    onMouseLeave={krachLoading ? undefined : () => setHoveredCell(null)}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{width: '100px', verticalAlign: 'bottom'}}></TableCell>
                            {krachLoading ? (
                                Array.from(new Array(numTeams)).map((_, index) => (
                                    <TableCell
                                        key={`pred-head-skel-${index}`}
                                        align="center"
                                        sx={{
                                            writingMode: 'vertical-rl',
                                            transform: 'rotate(180deg)',
                                            fontWeight: 'bold',
                                            whiteSpace: 'nowrap',
                                            height: '100px',
                                            padding: '8px 4px',
                                        }}
                                    ><Skeleton variant="text" sx={{ width: '80px', height: '1em' }} /></TableCell>
                                ))
                            ) : (
                                predictionTeams.map((team, colIndex) => {
                                    const isClicked = clickedCell && colIndex === clickedCell.col;
                                    const isHovered = hoveredCell && colIndex === hoveredCell.col;
                                    const displayName = team.substring(commonPrefix.length);
                                    const formattedDisplayName = formatTeamNameForDisplay(displayName);
                                    return (
                                        <TableCell
                                            key={team}
                                            align="center"
                                            sx={{
                                                writingMode: 'vertical-rl',
                                                transform: 'rotate(180deg)',
                                                fontWeight: 'bold',
                                                whiteSpace: 'nowrap',
                                                height: '100px',
                                                padding: '8px 4px',
                                                backgroundColor: isClicked ? 'action.selected' : isHovered ? 'action.hover' : 'inherit',
                                                transition: 'background-color 0.1s',
                                            }}
                                        >{formattedDisplayName}</TableCell>
                                    );
                                })
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {krachLoading ? (
                            Array.from(new Array(numTeams)).map((_, rowIndex) => (
                                <TableRow key={`pred-body-skel-${rowIndex}`}>
                                    <TableCell sx={{ fontWeight: 'bold' }}><Skeleton /></TableCell>
                                    {Array.from(new Array(numTeams)).map((_, colIndex) => (
                                        <TableCell key={colIndex} align="center"><Skeleton /></TableCell>
                                    ))}
                                </TableRow>
                                 ))
                        ) : (
                            predictionTeams.map((rowTeam, rowIndex) => {
                                const isClickedRow = clickedCell && rowIndex === clickedCell.row;
                                const isHoveredRow = hoveredCell && rowIndex === hoveredCell.row;
                                const displayRowName = rowTeam.substring(commonPrefix.length);
                                const formattedRowName = formatTeamNameForDisplay(displayRowName);
                                return (
                                    <TableRow key={rowTeam}>
                                        <TableCell component="th" scope="row" sx={{
                                            fontWeight: 'bold',
                                            backgroundColor: isClickedRow ? 'action.selected' : isHoveredRow ? 'action.hover' : 'inherit',
                                            transition: 'background-color 0.1s',
                                            fontSize: '0.8rem',
                                            padding: '4px 8px',
                                            whiteSpace: 'nowrap',
                                        }}>{formattedRowName}</TableCell>
                                        {predictionTeams.map((colTeam, colIndex) => {
                                            const predictionValue = krachData?.predictions?.[rowTeam]?.[colTeam];
                                            const isDiagonal = predictionValue === '--';
                                            const isUnavailable = !predictionValue || predictionValue === 'N/A';
                                            const isClickedCell = clickedCell && ((rowIndex === clickedCell.row && colIndex <= clickedCell.col) || (colIndex === clickedCell.col && rowIndex <= clickedCell.row));
                                            const isHoveredCell = hoveredCell && ((rowIndex === hoveredCell.row && colIndex <= hoveredCell.col) || (colIndex === hoveredCell.col && rowIndex <= hoveredCell.row));
                                            return (
                                                <TableCell
                                                    key={`${rowTeam}-${colTeam}`}
                                                    align="center"
                                                    onMouseEnter={() => !isDiagonal && !isUnavailable && setHoveredCell({ row: rowIndex, col: colIndex })}
                                                    onClick={() => {
                                                        if (isDiagonal || isUnavailable) return;
                                                        if (clickedCell && clickedCell.row === rowIndex && clickedCell.col === colIndex) {
                                                            setClickedCell(null);
                                                        } else {
                                                            logEvent('click_krach_matchup', { row_team: rowTeam, col_team: colTeam });
                                                            setClickedCell({ row: rowIndex, col: colIndex });
                                                        }
                                                    }}
                                                    sx={{
                                                        cursor: (isDiagonal || isUnavailable) ? 'default' : 'pointer',
                                                        backgroundColor: isDiagonal 
                                                            ? 'grey.200' 
                                                            : (isClickedCell ? 'action.selected' : isHoveredCell ? 'action.hover' : 'inherit'),
                                                        transition: 'background-color 0.1s',
                                                        fontSize: '0.75rem',
                                                        padding: '6px 2px',
                                                        color: isUnavailable && !isDiagonal ? 'text.disabled' : 'inherit',
                                                    }}
                                                >
                                                    {isDiagonal ? '' : (isUnavailable ? 'N/A' : predictionValue)}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', alignItems: 'center', mt: 4 }}>
                <Typography variant="h5" component="h2">
                    End Of Season Prediction
                </Typography>
                <Tooltip title="Final standings predicted using a Monte Carlo simulation of remaining games based on KRACH win probabilities.">
                    <IconButton size="small" aria-label="Learn more about season predictions" sx={{ ml: 0.5 }}>
                        <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                <Table size="small" sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.200' }}>
                            <TableCell>Rank</TableCell>
                            <TableCell>Team</TableCell>
                            <TableCell align="right">W</TableCell>
                            <TableCell align="right">L</TableCell>
                            <TableCell align="right">T</TableCell>
                            <TableCell align="right">PTS</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {predictedLoading ? (
                            Array.from(new Array(numTeams)).map((_, index) => (
                                <TableRow key={`predict-skel-${index}`} sx={{ '&:nth-of-type(even)': { backgroundColor: 'action.hover' } }}>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell>
                                </TableRow>
                            ))
                        ) : (
                            predictedStandings.map((team) => {
                                const isJets = team.team.includes(teamName);
                                const rowSx = { '&:nth-of-type(even)': { backgroundColor: 'action.hover' }, '& > *': { fontWeight: 'normal' } };
                                const borderStyle = '2px solid #8884d8';
                                const radius = '8px';
                                return (
                                <TableRow key={team.team} sx={rowSx}>
                                    <TableCell sx={isJets ? { borderTop: borderStyle, borderBottom: borderStyle, borderLeft: borderStyle, borderTopLeftRadius: radius, borderBottomLeftRadius: radius } : {}}>{team.rank}</TableCell>
                                    <TableCell sx={isJets ? { borderTop: borderStyle, borderBottom: borderStyle } : {}}>{team.team}</TableCell>
                                    <TableCell align="right" sx={isJets ? { borderTop: borderStyle, borderBottom: borderStyle } : {}}>{team.wins}</TableCell>
                                    <TableCell align="right" sx={isJets ? { borderTop: borderStyle, borderBottom: borderStyle } : {}}>{team.losses}</TableCell>
                                    <TableCell align="right" sx={isJets ? { borderTop: borderStyle, borderBottom: borderStyle } : {}}>{team.ties}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', ...(isJets && { borderTop: borderStyle, borderBottom: borderStyle, borderRight: borderStyle, borderTopRightRadius: radius, borderBottomRightRadius: radius }) }}>{team.points}</TableCell>
                                </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

             <Box ref={trendChartRef} sx={{ display: 'flex', alignItems: 'center', mt: 4, mb: 0.5 }}>
                <Typography variant="h5" component="h2">
                    {teamName}{teamName.endsWith('s') ? "'" : "'s"} Predicted Rank Trend
                </Typography>
                <Tooltip title={`Predicted final rank for ${teamName} calculated at the end of each week of the season. Whisker error bars show the 80% confidence interval (p10 optimistic – p90 pessimistic finish) from 200 Monte Carlo simulations based on KRACH win probabilities.`}>
                    <IconButton size="small" sx={{ ml: 0.5 }} aria-label="Learn more about rank trend">
                        <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Whisker error bars indicate the 80% confidence interval (p10 – p90 finish across 200 simulations).
            </Typography>
            {trendLoading && <LinearProgress variant="determinate" value={trendProgress} />}
            <Paper variant="outlined" sx={{ p: 2, mb: 4, height: 300, position: 'relative' }}>
                {trendError ? (
                    <Alert severity="error" sx={{ m: 1 }}>Could not load trend data: {trendError}</Alert>
                ) : (
                    <LineChart
                      slotProps={{
                            noDataOverlay: { message: 'Loading...' },
                            tooltip: {trigger: 'item' },
                        }}
                        dataset={chartDataset}
                        margin={{ top: 10, right: 10, left: -10, bottom: 10 }}
                        xAxis={[{
                            dataKey: 'week',
                            min: 0,
                            max: totalSeasonWeeks > 0 ? totalSeasonWeeks : 1,
                            valueFormatter: (value: number) => minDate ? formatWeekToDate(minDate, value) : '',
                            tickInterval: (value, index) => index % 4 === 0 || index === totalSeasonWeeks -1,
                        }]}
                        yAxis={[{
                            scaleType: 'linear',
                            reverse: true,
                            min: 0,
                            max: yAxisMax + 0.5,
                            tickInterval: yAxisTicks,
                            valueFormatter: (value: number) => formatRank(value),
                        }]}
                        series={seriesConfig}
                        grid={{ vertical: true, horizontal: true }}
                        sx={{
                            '.MuiLineElement-series-playoffCutoffSeries': {
                                strokeDasharray: '3 3',
                            },
                        }}
                    >
                        <RankErrorBars data={jetsPredictionTrend} />
                    </LineChart>
                )}
            </Paper>
        </Box>
    );
};

export default KrachRankings;
